import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { OptionGroupType } from '@frontend/sports/betting-offer/feature/fixture-factories';
import { EventModel } from '@frontend/sports/betting-offer/feature/model';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';

import { loadPlayerStatisticsData } from './player-statistics.actions';
import { PlayerStatisticsStoreData } from './player-statistics.model';
import { PlayerStatisticsService } from './player-statistics.service';
import { selectDataByEventAndMarket } from './player-statistics.state';

@Injectable()
export class BaseOptionGroupStatisticsService {
    protected readonly store = inject(Store);
    protected readonly destroyRef = inject(DestroyRef);
    protected readonly playerStatisticsService = inject(PlayerStatisticsService);
    private playerStatisticsSubscription: Subscription;

    // Player statistics properties
    readonly isPlayerStatisticsEnabled = signal(false);
    readonly isSeasonStatisticsEnabled = signal(false);
    readonly playerStatistics = signal<PlayerStatisticsStoreData>({
        isLoading: true,
        isError: false,
        data: undefined,
    });

    initializePlayerStatistics(event: EventModel, optionGroupType: OptionGroupType): void {
        this.isPlayerStatisticsEnabled.set(this.playerStatisticsService.isPlayerStatisticsVisible(event.league.id, optionGroupType));
        this.isSeasonStatisticsEnabled.set(this.playerStatisticsService.isSeasonStatisticsVisible(event.league.id, optionGroupType));
    }

    fetchPlayerStatistics(event: EventModel, optionGroup: any, mainMarket = false): void {
        if (this.isPlayerStatisticsEnabled() || this.isSeasonStatisticsEnabled()) {
            const eventId = event.id;
            const marketFilter = optionGroup.originalId;
            if (mainMarket) {
                this.store.dispatch(loadPlayerStatisticsData({ eventId, mainMarket: true }));
            } else {
                this.store.dispatch(loadPlayerStatisticsData({ eventId, marketFilter }));
            }

            this.subscribeToPlayerStatisticsState(eventId, marketFilter, mainMarket);
        }
    }

    private subscribeToPlayerStatisticsState(eventId: string, marketFilter: string, mainMarket = false): void {
        this.playerStatisticsSubscription?.unsubscribe();
        this.playerStatisticsSubscription = this.store
            .select(selectDataByEventAndMarket(eventId, marketFilter, mainMarket))
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((data: PlayerStatisticsStoreData) => {
                if (data) {
                    this.playerStatistics.set({
                        isLoading: data.isLoading,
                        isError: data.isError,
                        data: data.data,
                    });
                }
            });
    }
}
